import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  BookOpen,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  CreditCard,
  Video,
  Users,
  FolderOpen,
} from "lucide-react";
import { useState } from "react";

// WEDO Logo Component
const WedoLogo = ({ variant = "color", className = "" }) => {
  const logoSrc = variant === "white" 
    ? "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/bdxr35h3_2024_10_make_it_matter.png"
    : "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/fb48vbve_2024_10_make_it_matter_bleu.png";
  
  return (
    <img 
      src={logoSrc} 
      alt="WEDO - Make it matter" 
      className={className}
    />
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: "/profile", label: t('nav.profile'), icon: User },
  ];

  const adminItems = [
    { path: "/admin", label: t('admin.dashboard.title'), icon: Shield },
    { path: "/admin/programs", label: "Programmes", icon: FolderOpen },
    { path: "/admin/coaching", label: "Suivi Coaching", icon: Users },
    { path: "/admin/users", label: t('nav.users'), icon: User },
    { path: "/admin/payments", label: t('nav.payments'), icon: CreditCard },
    { path: "/admin/modules", label: t('nav.modules'), icon: BookOpen },
    { path: "/admin/modules-manager", label: t('nav.videosImport'), icon: Video },
  ];

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <WedoLogo variant="white" className="h-10 w-auto" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/70 hover:bg-white/5 hover:text-white"
              }`
            }
            data-testid={`nav-${item.path.replace("/", "")}`}
          >
            <item.icon className="w-5 h-5 mr-3" />
            {item.label}
          </NavLink>
        ))}

        {/* Admin Section */}
        {user?.role === "admin" && (
          <>
            <div className="pt-4 pb-2">
              <p className="text-xs text-white/40 uppercase tracking-wider px-4">
                {t('nav.admin')}
              </p>
            </div>
            {adminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-[#7FD858]/20 to-[#EC407A]/20 text-white border border-white/10"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
                data-testid={`nav-${item.path.replace("/", "").replace("/", "-")}`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center space-x-3 mb-4 px-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-10 h-10 rounded-xl object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <span className="text-white font-medium">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/50 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5 rounded-xl"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          {t('nav.logout')}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#101E4D] border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <WedoLogo variant="white" className="h-8 w-auto" />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#101E4D] pt-16">
          <div className="flex flex-col h-full">
            <NavContent />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-[#101E4D]">
        <NavContent />
      </aside>

      {/* Mobile spacer */}
      <div className="lg:hidden h-14"></div>
    </>
  );
};

export default Sidebar;
